import * as React from "react";
import { Link } from "gatsby";

import { Button } from "../ui/button";
import { cn } from "../../lib/cn";
import {
  acceptAnalyticsCookies,
  COOKIE_CONSENT_OPEN_EVENT,
  openCookieConsentSettings,
  readCookieConsent,
  rejectAnalyticsCookies,
  writeCookieConsent,
} from "../../lib/cookie-consent";
import {
  initializeAnalyticsFromConsent,
  setAnalyticsConsent,
  trackPageView,
} from "../../lib/analytics";

function CookiePreferencesDialog({
  analyticsEnabled,
  isOpen,
  onAnalyticsChange,
  onClose,
  onSave,
}: {
  analyticsEnabled: boolean;
  isOpen: boolean;
  onAnalyticsChange: (value: boolean) => void;
  onClose: () => void;
  onSave: () => void;
}) {
  if (!isOpen) {
    return null;
  }

  return (
    <div
      aria-labelledby="cookie-preferences-title"
      aria-modal="true"
      className="fixed inset-0 z-[70] flex items-end justify-center bg-black/70 px-4 py-4 md:items-center"
      role="dialog"
    >
      <div className="w-full max-w-2xl rounded-brand-md border border-border-strong bg-surface-1 p-6">
        <div className="grid gap-6">
          <div className="grid gap-3">
            <p className="m-0 font-display text-[0.78rem] font-bold tracking-[0.18em] text-primary uppercase">
              Preferências de cookies
            </p>
            <h2
              className="m-0 font-display text-[clamp(1.6rem,4vw,2.2rem)] leading-[1.02] text-text-strong uppercase"
              id="cookie-preferences-title"
            >
              Escolha como seus dados serão usados
            </h2>
            <p className="m-0 text-sm leading-7 text-text-base">
              Você pode manter apenas os cookies necessários ou autorizar os
              cookies analíticos para nos ajudar a entender o uso do site.
            </p>
          </div>

          <div className="grid gap-4">
            <div className="rounded-brand-md border border-border-soft bg-canvas/60 p-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="grid gap-2 md:w-3/5">
                  <p className="m-0 font-display text-sm font-bold tracking-[0.08em] text-text-strong uppercase">
                    Cookies necessários
                  </p>
                  <p className="m-0 text-sm leading-6 text-text-muted">
                    Mantêm o funcionamento básico do site e salvam sua escolha
                    de consentimento.
                  </p>
                </div>
                <span className="inline-flex min-h-10 items-center justify-center rounded-full border border-border-soft bg-surface-2 px-4 font-display text-[0.78rem] font-bold tracking-[0.08em] text-text-strong uppercase md:w-2/5">
                  Sempre ativos
                </span>
              </div>
            </div>

            <div className="rounded-brand-md border border-border-soft bg-canvas/60 p-4">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="grid gap-2">
                  <p className="m-0 font-display text-sm font-bold tracking-[0.08em] text-text-strong uppercase">
                    Cookies analíticos
                  </p>
                  <p className="m-0 text-sm leading-6 text-text-muted">
                    Medem visitas, páginas acessadas e interações gerais com o
                    site usando Google Analytics 4.
                  </p>
                </div>
                <Button
                  aria-pressed={analyticsEnabled}
                  className={cn(
                    "min-h-10 min-w-[9.5rem]",
                    analyticsEnabled
                      ? "border-primary bg-primary text-text-strong hover:bg-primary-hover"
                      : "border-border-soft bg-surface-2 text-text-base hover:text-text-strong",
                  )}
                  onClick={() => onAnalyticsChange(!analyticsEnabled)}
                >
                  {analyticsEnabled ? "Ativado" : "Desativado"}
                </Button>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 md:flex-row md:justify-end">
            <Button onClick={onClose} variant="ghost">
              Fechar
            </Button>
            <Button onClick={onSave}>Salvar preferências</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CookieConsentManager() {
  const [consent, setConsent] = React.useState(readCookieConsent);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = React.useState(
    () => readCookieConsent().analytics,
  );

  React.useEffect(() => {
    initializeAnalyticsFromConsent();
  }, []);

  React.useEffect(() => {
    function handleOpenPreferences() {
      const latestConsent = readCookieConsent();
      setConsent(latestConsent);
      setAnalyticsEnabled(latestConsent.analytics);
      setIsDialogOpen(true);
    }

    window.addEventListener(COOKIE_CONSENT_OPEN_EVENT, handleOpenPreferences);

    return () => {
      window.removeEventListener(
        COOKIE_CONSENT_OPEN_EVENT,
        handleOpenPreferences,
      );
    };
  }, []);

  const handleAccept = React.useCallback(() => {
    const nextConsent = acceptAnalyticsCookies();
    setConsent(nextConsent);
    setAnalyticsEnabled(true);
    setIsDialogOpen(false);
    setAnalyticsConsent(true);
    trackPageView();
  }, []);

  const handleReject = React.useCallback(() => {
    const nextConsent = rejectAnalyticsCookies();
    setConsent(nextConsent);
    setAnalyticsEnabled(false);
    setIsDialogOpen(false);
    setAnalyticsConsent(false);
  }, []);

  const handleSavePreferences = React.useCallback(() => {
    const nextConsent = writeCookieConsent({
      analytics: analyticsEnabled,
      answered: true,
    });

    setConsent(nextConsent);
    setIsDialogOpen(false);

    if (analyticsEnabled) {
      setAnalyticsConsent(true);
      trackPageView();
      return;
    }

    setAnalyticsConsent(false);
  }, [analyticsEnabled]);

  return (
    <>
      {!consent.answered ? (
        <div className="fixed inset-x-0 bottom-0 z-[60] p-5 md:max-w-[420px] md:p-8">
          <div className="rounded-brand-md border border-border-strong bg-surface-1 p-5">
            <div className="grid items-start gap-5">
              <div className="grid gap-3">
                <p className="m-0 font-display text-[0.78rem] font-bold tracking-[0.18em] text-primary uppercase">
                  Cookies e privacidade
                </p>
                <div className="grid gap-2">
                  <p className="m-0 max-w-3xl text-sm leading-7 text-text-base">
                    Nós utilizamos cookies para melhorar a navegação, desempenho
                    e personalizar o conteúdo. Você pode aceitar, recusar ou
                    ajustar sua escolha a qualquer momento.
                  </p>
                </div>
                <p className="m-0 text-sm leading-6 text-text-muted">
                  Leia a nossa{" "}
                  <Link
                    className="font-semibold text-primary decoration-primary/60 underline-offset-4"
                    to="/politica-de-cookies/"
                  >
                    Política de Cookies
                  </Link>
                  .
                </p>
              </div>

              <div className="grid gap-3 md:min-w-[15rem] md:grid-cols-2">
                <Button onClick={handleAccept}>Aceitar</Button>
                <Button onClick={handleReject} variant="secondary">
                  Recusar
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <CookiePreferencesDialog
        analyticsEnabled={analyticsEnabled}
        isOpen={isDialogOpen}
        onAnalyticsChange={setAnalyticsEnabled}
        onClose={() => setIsDialogOpen(false)}
        onSave={handleSavePreferences}
      />
    </>
  );
}

export function CookieSettingsLink({ className }: { className?: string }) {
  return (
    <Button className={className} onClick={openCookieConsentSettings}>
      Gerenciar cookies
    </Button>
  );
}
